import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          <a href="/" className="inline-block">
            <img src="https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/718pfmf4_New%20Company%20Logo.svg" alt="RoLink" className="h-10 mx-auto" />
          </a>
          <p className="text-sm text-[#9CA3AF] mt-3">Admin Panel Access</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 bg-[#111111] border border-[#1F2937] p-8" data-testid="login-form">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="login-error">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          <div>
            <Label className="text-[#9CA3AF] text-xs uppercase tracking-[0.15em]">Email</Label>
            <Input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="mt-1.5 bg-[#050505] border-[#1F2937] text-[#F3F4F6] h-11 rounded-none focus:border-[#EE1D25]"
              placeholder="admin@rolinkbiotechnology.com"
            />
          </div>

          <div>
            <Label className="text-[#9CA3AF] text-xs uppercase tracking-[0.15em]">Password</Label>
            <Input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              className="mt-1.5 bg-[#050505] border-[#1F2937] text-[#F3F4F6] h-11 rounded-none focus:border-[#EE1D25]"
            />
          </div>

          <Button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-[#EE1D25] hover:bg-[#CC1920] text-white h-11 rounded-none font-medium"
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>

        <p className="text-center mt-6">
          <a href="/" className="text-xs text-[#4B5563] hover:text-[#9CA3AF] transition-colors">&larr; Back to website</a>
        </p>
      </div>
    </div>
  );
}
